import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { onboardingAPI } from '../../services/api';
import { STAGE_LABELS, STAGE_COLORS } from './Onboarding';
import { parseApiError } from '../../utils/errors';
import { copyText } from '../../utils/clipboard';
import { fmtDate } from '../../utils/dateUtils';

const STAGE_ORDER = ['new_entry', 'tutor_suggestion', 'verification', 'demo_scheduled', 'demo_review', 'class_started'];

export default function OnboardingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  const load = () => onboardingAPI.get(id).then(({ data }) => setRec(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setRec({ ...rec, [k]: v });
  };

  const buildPayload = (r) => ({
    suggested_tutor: r.suggested_tutor, alternative_tutor: r.alternative_tutor,
    subject_match: r.subject_match, location_match: r.location_match, suggestion_notes: r.suggestion_notes,
    tutor_availability_confirmed: r.tutor_availability_confirmed,
    subject_requirement_matched: r.subject_requirement_matched,
    location_feasibility_checked: r.location_feasibility_checked,
    schedule_feasibility_checked: r.schedule_feasibility_checked,
    demo_date: r.demo_date || null, demo_time: r.demo_time || null, assigned_tutor: r.assigned_tutor,
    demo_review: r.demo_review || null, start_date: r.start_date || null,
  });

  const save = async (extra = {}, successMsg = 'Saved') => {
    setSaving(true);
    setErr('');
    try {
      const { data } = await onboardingAPI.update(id, { ...buildPayload(rec), ...extra });
      setRec(data);
      setMsg(successMsg);
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setErr(parseApiError(e, 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  const reassign = async () => {
    if (!window.confirm('Reassign tutor? The current assignment will be saved to history and the Tutor Suggestion stage will reopen for editing.')) return;
    setSaving(true);
    setErr('');
    try {
      const { data } = await onboardingAPI.reassign(id);
      setRec(data);
      setMsg('Tutor suggestion reopened for reassignment');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setErr(parseApiError(e, 'Reassignment failed'));
    } finally {
      setSaving(false);
    }
  };

  const copyLocation = async () => {
    if (await copyText(rec.location_link)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this onboarding record?')) return;
    await onboardingAPI.remove(id);
    navigate('/onboarding');
  };

  if (loading) return <div className="page-loading">Loading...</div>;
  if (!rec) return <div className="page"><div className="empty-state">Record not found.</div></div>;

  const offline = rec.mode === 'offline';
  const currentIdx = STAGE_ORDER.indexOf(rec.stage);
  const onboardingComplete = rec.stage === 'class_started' && rec.is_active;

  // 'completed' | 'current' | 'pending'
  const stageState = (i) => {
    if (onboardingComplete || i < currentIdx) return 'completed';
    if (i === currentIdx) return 'current';
    return 'pending';
  };
  const isEditable = (i) => stageState(i) === 'current';

  const StatusBadge = ({ state }) => {
    if (state === 'completed') return <span className="badge badge-green">Completed ✅</span>;
    if (state === 'current') return <span className="badge badge-orange">In Progress</span>;
    return <span className="badge badge-gray">Pending</span>;
  };

  let history = [];
  try { history = rec.tutor_history ? JSON.parse(rec.tutor_history) : []; } catch { history = []; }

  const demoReady = !!(rec.demo_date && rec.demo_time);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2>{rec.student_name}</h2>
          <p className="text-muted">
            {rec.student_class ? `Class ${rec.student_class} · ` : ''}
            {rec.syllabus ? rec.syllabus.toUpperCase() + ' · ' : ''}
            <span className={`badge badge-${rec.mode === 'online' ? 'blue' : 'teal'}`}>{rec.mode}</span>
            {rec.is_on_hold && <span className="badge badge-red ml-2">On Hold</span>}
            {rec.reassignment_count > 0 && <span className="badge badge-purple ml-2">Reassigned 🔄 ×{rec.reassignment_count}</span>}
          </p>
        </div>
        <button className="btn btn-ghost" onClick={() => navigate('/onboarding')}>← Back</button>
      </div>

      {msg && <div className="alert alert-success">{msg}</div>}
      {err && <div className="alert alert-danger">{err}</div>}

      {/* Overall status banner */}
      <div className="card mb-4" style={{ padding: '14px 18px' }}>
        {onboardingComplete ? (
          <strong style={{ fontSize: 16 }}>✅ Onboarding Completed</strong>
        ) : (
          <strong style={{ fontSize: 16 }}>Current Stage: {STAGE_LABELS[rec.stage]}</strong>
        )}
        <div className="filter-bar mt-2" style={{ flexWrap: 'wrap' }}>
          {STAGE_ORDER.map((s, i) => {
            const st = stageState(i);
            return (
              <span key={s} className={`badge badge-${st === 'completed' ? 'green' : st === 'current' ? STAGE_COLORS[s] : 'gray'}`}>
                {i + 1}. {STAGE_LABELS[s]} {st === 'completed' ? '✅' : st === 'current' ? '●' : '○'}
              </span>
            );
          })}
        </div>
      </div>

      {/* Student information */}
      <div className="card mb-4">
        <div className="card-header"><h3>Student Information <span className="badge badge-green">Completed ✅</span></h3></div>
        <div style={{ padding: '12px 16px' }}>
          <p><strong>Parent number:</strong> {rec.parent_number || '—'}</p>
          <p><strong>Subjects required:</strong> {rec.subjects_required || '—'}</p>
          <p><strong>Remarks:</strong> {rec.remarks || '—'}</p>
          {offline && (
            <>
              <hr />
              <p><strong>Full location:</strong> {rec.full_location || '—'}</p>
              <p><strong>Nearby locations:</strong> {rec.nearby_locations || '—'}</p>
              <p><strong>Landmark:</strong> {rec.landmark || '—'}</p>
              <p><strong>Travel notes:</strong> {rec.travel_notes || '—'}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <strong>Location link:</strong>
                {rec.location_link ? (
                  <>
                    <a href={rec.location_link} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>{rec.location_link}</a>
                    <button className="btn btn-sm btn-ghost" onClick={copyLocation}>{copied ? '✓ Copied' : '📋 Copy Location'}</button>
                  </>
                ) : '—'}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Stage 2: Tutor Suggestion */}
      <div className="card mb-4">
        <div className="card-header"><h3>Stage 2 · Tutor Suggestion <StatusBadge state={stageState(1)} /></h3></div>
        <div style={{ padding: '12px 16px' }}>
          <div className="form-row">
            <div className="form-group">
              <label>Suggested Tutor</label>
              <input value={rec.suggested_tutor || ''} onChange={set('suggested_tutor')} disabled={!isEditable(1)} />
            </div>
            <div className="form-group">
              <label>Alternative Tutor (optional)</label>
              <input value={rec.alternative_tutor || ''} onChange={set('alternative_tutor')} disabled={!isEditable(1)} />
            </div>
          </div>
          <div className="form-row">
            <label className="checkbox-label"><input type="checkbox" checked={rec.subject_match} onChange={set('subject_match')} disabled={!isEditable(1)} /> Subject Match</label>
            {offline && <label className="checkbox-label"><input type="checkbox" checked={rec.location_match} onChange={set('location_match')} disabled={!isEditable(1)} /> Location Match</label>}
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea rows={2} value={rec.suggestion_notes || ''} onChange={set('suggestion_notes')} disabled={!isEditable(1)} />
          </div>

          {history.length > 0 && (
            <div style={{ marginTop: 8, padding: 10, background: 'var(--bg-subtle, #f5f6f8)', borderRadius: 8 }}>
              <div className="text-muted" style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Reassignment History</div>
              {history.map((h, idx) => (
                <div key={idx} style={{ fontSize: 13, marginBottom: 4 }}>
                  #{idx + 1}: Suggested <strong>{h.suggested_tutor || '—'}</strong>
                  {h.alternative_tutor ? `, alt ${h.alternative_tutor}` : ''}
                  {h.assigned_tutor ? `, assigned ${h.assigned_tutor}` : ''}
                  {h.reassigned_at ? ` — ${fmtDate(h.reassigned_at)}` : ''}
                  {h.reassigned_by ? ` by ${h.reassigned_by}` : ''}
                </div>
              ))}
            </div>
          )}

          <div className="form-actions">
            {isEditable(1) ? (
              <>
                <button className="btn btn-ghost" disabled={saving} onClick={() => save({}, 'Tutor suggestion saved')}>Save / Add Notes</button>
                <button className="btn btn-primary" disabled={saving} onClick={() => save({ stage: 'verification' }, 'Tutor suggested → Verification')}>Suggest Tutor → Verify</button>
              </>
            ) : stageState(1) === 'completed' && !onboardingComplete ? (
              <button className="btn btn-ghost" disabled={saving} onClick={reassign}>🔄 Reassign Tutor (Correct)</button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Stage 3: Verification */}
      <div className="card mb-4">
        <div className="card-header"><h3>Stage 3 · Verification <StatusBadge state={stageState(2)} /></h3></div>
        <div style={{ padding: '12px 16px' }}>
          <label className="checkbox-label"><input type="checkbox" checked={rec.tutor_availability_confirmed} onChange={set('tutor_availability_confirmed')} disabled={!isEditable(2)} /> Tutor Availability Confirmed</label>
          <label className="checkbox-label"><input type="checkbox" checked={rec.subject_requirement_matched} onChange={set('subject_requirement_matched')} disabled={!isEditable(2)} /> Subject Requirement Matched</label>
          {offline && <label className="checkbox-label"><input type="checkbox" checked={rec.location_feasibility_checked} onChange={set('location_feasibility_checked')} disabled={!isEditable(2)} /> Location Feasibility Checked</label>}
          <label className="checkbox-label"><input type="checkbox" checked={rec.schedule_feasibility_checked} onChange={set('schedule_feasibility_checked')} disabled={!isEditable(2)} /> Schedule Feasibility Checked</label>
          {isEditable(2) && (
            <div className="form-actions">
              <button className="btn btn-ghost" disabled={saving} onClick={reassign}>Reassign Tutor</button>
              <button className="btn btn-ghost" disabled={saving} onClick={() => save({ is_on_hold: !rec.is_on_hold }, rec.is_on_hold ? 'Hold released' : 'Put on hold')}>{rec.is_on_hold ? 'Release Hold' : 'Hold'}</button>
              <button className="btn btn-primary" disabled={saving} onClick={() => save({ stage: 'demo_scheduled', is_on_hold: false }, 'Verified → Demo')}>Verified → Schedule Demo</button>
            </div>
          )}
        </div>
      </div>

      {/* Stage 4: Demo Class */}
      <div className="card mb-4">
        <div className="card-header"><h3>Stage 4 · Demo Class <StatusBadge state={stageState(3)} /></h3></div>
        <div style={{ padding: '12px 16px' }}>
          <div className="form-row">
            <div className="form-group">
              <label>Demo Date *</label>
              <input type="date" value={rec.demo_date || ''} onChange={set('demo_date')} disabled={!isEditable(3)} />
              {isEditable(3) && !rec.demo_date && <span className="field-error">Please select a Demo Date.</span>}
            </div>
            <div className="form-group">
              <label>Demo Time *</label>
              <input type="time" value={rec.demo_time || ''} onChange={set('demo_time')} disabled={!isEditable(3)} />
              {isEditable(3) && !rec.demo_time && <span className="field-error">Please select a Demo Time.</span>}
            </div>
            <div className="form-group">
              <label>Assigned Tutor</label>
              <input value={rec.assigned_tutor || ''} onChange={set('assigned_tutor')} disabled={!isEditable(3)} />
            </div>
          </div>
          <label className="checkbox-label">
            <input type="checkbox" checked={!!rec.mobile_app_access_provided}
              onChange={(e) => save({ mobile_app_access_provided: e.target.checked }, 'Mobile app access updated')} disabled={saving} />
            Mobile App Access Provided
          </label>
          {rec.demo_completed && <p className="badge badge-green" style={{ marginTop: 8 }}>Demo Completed</p>}
          {isEditable(3) && (
            <div className="form-actions">
              <button className="btn btn-ghost" disabled={saving || !demoReady} onClick={() => save({ stage: 'demo_scheduled' }, 'Demo scheduled')}>Schedule / Reschedule Demo</button>
              <button className="btn btn-primary" disabled={saving || !demoReady} onClick={() => save({ demo_completed: true, stage: 'demo_review' }, 'Demo marked completed')}>Mark Demo Completed</button>
            </div>
          )}
        </div>
      </div>

      {/* Stage 5: Demo Review */}
      <div className="card mb-4">
        <div className="card-header"><h3>Stage 5 · Demo Review <StatusBadge state={stageState(4)} /></h3></div>
        <div style={{ padding: '12px 16px' }}>
          <div className="form-group">
            <label>Review Outcome</label>
            <select value={rec.demo_review || ''} onChange={set('demo_review')} disabled={!isEditable(4)}>
              <option value="">Select…</option>
              <option value="successful">Successful</option>
              <option value="need_another_tutor">Need Another Tutor</option>
              <option value="student_not_interested">Student Not Interested</option>
              <option value="pending_decision">Pending Decision</option>
            </select>
          </div>
          {isEditable(4) && (
            <div className="form-actions">
              <button className="btn btn-ghost" disabled={saving} onClick={() => save({}, 'Review saved')}>Save Review</button>
              {rec.demo_review === 'need_another_tutor' && (
                <button className="btn btn-ghost" disabled={saving} onClick={reassign}>Find Another Tutor</button>
              )}
              <button className="btn btn-primary" disabled={saving || rec.demo_review !== 'successful'} onClick={() => save({ stage: 'class_started' }, 'Moving to Class Started')}>Proceed → Class Started</button>
            </div>
          )}
        </div>
      </div>

      {/* Stage 6: Class Started */}
      <div className="card mb-4">
        <div className="card-header"><h3>Stage 6 · Class Started <StatusBadge state={stageState(5)} /></h3></div>
        <div style={{ padding: '12px 16px' }}>
          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input type="date" value={rec.start_date || ''} onChange={set('start_date')} disabled={!isEditable(5)} />
            </div>
            <div className="form-group">
              <label>Assigned Tutor</label>
              <input value={rec.assigned_tutor || ''} onChange={set('assigned_tutor')} disabled={!isEditable(5)} />
            </div>
          </div>
          <div className="form-row">
            <label className="checkbox-label">
              <input type="checkbox" checked={!!rec.web_access_parent}
                onChange={(e) => save({ web_access_parent: e.target.checked }, 'Parent access updated')} disabled={saving} />
              Web App Access Provided to Parent
            </label>
            <label className="checkbox-label">
              <input type="checkbox" checked={!!rec.web_access_companion}
                onChange={(e) => save({ web_access_companion: e.target.checked }, 'Companion access updated')} disabled={saving} />
              Web App Access Provided to Companion
            </label>
          </div>
          {rec.is_active && <p className="badge badge-green">Active</p>}
          {isEditable(5) && (
            <div className="form-actions">
              <button className="btn btn-primary" disabled={saving} onClick={() => save({ stage: 'class_started', is_active: true }, 'Class started — student active')}>Start Class (Mark Active)</button>
            </div>
          )}
        </div>
      </div>

      <div className="form-actions">
        <button className="btn btn-danger" onClick={remove}>Delete Record</button>
      </div>
    </div>
  );
}
